<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRoute, RouterLink, useRouter } from 'vue-router';

const { proxy } = getCurrentInstance();
const { $stores, $bus } = proxy;

const route = useRoute();
const router = useRouter();
const productId = route.params.id;

// Accéder aux stores via le proxy
const productsStore = $stores?.products;
const cartStore = $stores?.cart;

const product = ref(null);
const selectedDuration = ref('1 mois');
const quantity = ref(1);
const loading = ref(true);

const durations = ['1 mois', '3 mois', '6 mois', '12 mois'];

const relatedProducts = ref([]);

onMounted(async () => {
  try {
    let allProducts = productsStore?.state?.allProducts || [];

    // Si le store est vide, charger tous les produits d'abord
    if (!allProducts.length) {
      console.log('Store vide, chargement des produits...');
      allProducts = await productsStore?.fetchProducts() || [];
    }

    // Chercher le produit par ID
    let foundProduct = allProducts.find(p => p.id === productId);

    if (foundProduct) {
      product.value = foundProduct;
    } else {
      // Produit par défaut si non trouvé
      product.value = {
        id: productId,
        name: 'Abonnement Premium',
        title: 'Abonnement Premium',
        price: 2500,
        priceDisplay: '2 500 CFA / mois',
        description: 'Profitez d\'un accès illimité à notre catalogue premium.',
        features: ['Accès illimité', 'HD/4K', 'Multi-écrans', 'Téléchargement offline', 'Sans publicité'],
        image: '',
        unitPeriod: 'mois',
      };
    }

    // Produits associés (autres produits du même type)
    const sameCategoryProducts = allProducts.filter(p => p.category === foundProduct?.category && p.id !== productId);
    relatedProducts.value = sameCategoryProducts.slice(0, 5);
  } catch (error) {
    console.error('Erreur chargement produit:', error);
  } finally {
    loading.value = false;
  }
});

const totalPrice = computed(() => {
  if (!product.value) return 0;
  const multiplier = selectedDuration.value.includes('3') ? 3 : selectedDuration.value.includes('6') ? 6 : selectedDuration.value.includes('12') ? 12 : 1;
  return product.value.price * multiplier * quantity.value;
});

const addToCart = () => {
  if (!product.value) return;

  // Préparer les options
  const options = {
    duration: selectedDuration.value,
  };

  // Ajouter au panier via le store
  cartStore?.addItem(product.value, quantity.value, options);

  // Notification visuelle (optionnel)
  window.$bus?.emit('cart:item:added', {
    product: product.value,
    quantity: quantity.value,
    options,
  });
};

const buyNow = () => {
  // D'abord ajouter au panier
  addToCart();

  // Puis rediriger vers le checkout
  router.push('/checkout');
};
</script>

<template>
  <div class="min-h-screen" style="background: #0A0A0B;">
    <!-- Loading Skeleton -->
    <div v-if="loading" class="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <div class="animate-pulse space-y-10">
        <!-- Breadcrumb skeleton -->
        <div class="flex items-center gap-2 text-sm">
          <div class="h-4 w-20 rounded bg-white/10"></div>
          <div class="h-4 w-2 rounded-full bg-white/10"></div>
          <div class="h-4 w-20 rounded bg-white/10"></div>
          <div class="h-4 w-2 rounded-full bg-white/10"></div>
          <div class="h-4 w-32 rounded bg-white/10"></div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <!-- Image skeleton -->
          <div class="lg:col-span-5">
            <div class="aspect-square rounded-3xl bg-white/5"></div>
          </div>

          <!-- Info skeleton -->
          <div class="lg:col-span-4 space-y-6">
            <div class="flex items-center gap-3">
              <div class="h-7 w-32 rounded-full bg-white/5"></div>
              <div class="h-5 w-40 rounded bg-white/5"></div>
            </div>
            <div class="h-12 w-3/4 rounded bg-white/5"></div>
            <div class="space-y-3">
              <div class="h-4 w-full rounded bg-white/5"></div>
              <div class="h-4 w-5/6 rounded bg-white/5"></div>
              <div class="h-4 w-4/6 rounded bg-white/5"></div>
            </div>
            <div class="space-y-4 pt-4">
              <div class="flex items-center gap-3">
                <div class="h-8 w-8 rounded-lg bg-white/5"></div>
                <div class="h-4 w-40 rounded bg-white/5"></div>
              </div>
              <div class="flex items-center gap-3">
                <div class="h-8 w-8 rounded-lg bg-white/5"></div>
                <div class="h-4 w-36 rounded bg-white/5"></div>
              </div>
              <div class="flex items-center gap-3">
                <div class="h-8 w-8 rounded-lg bg-white/5"></div>
                <div class="h-4 w-44 rounded bg-white/5"></div>
              </div>
            </div>
          </div>

          <!-- Purchase card skeleton -->
          <div class="lg:col-span-3">
            <div class="rounded-3xl p-8 space-y-6 bg-white/5">
              <div class="space-y-2">
                <div class="h-4 w-20 rounded bg-white/5"></div>
                <div class="h-10 w-40 rounded bg-white/5"></div>
              </div>
              <div class="space-y-4">
                <div class="grid grid-cols-2 gap-2">
                  <div class="h-12 rounded-xl bg-white/5"></div>
                  <div class="h-12 rounded-xl bg-white/5"></div>
                  <div class="h-12 rounded-xl bg-white/5"></div>
                  <div class="h-12 rounded-xl bg-white/5"></div>
                </div>
                <div class="h-14 rounded-xl bg-white/5"></div>
                <div class="h-14 rounded-2xl bg-white/5"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Content -->
    <div v-else-if="product" class="max-w-7xl mx-auto px-6 lg:px-8 py-12">

      <!-- Breadcrumb -->
      <nav aria-label="Breadcrumb" class="flex items-center text-sm text-white/50 mb-10">
        <RouterLink class="hover:text-white transition-colors" to="/">Accueil</RouterLink>
        <span class="mx-2">/</span>
        <RouterLink class="hover:text-white transition-colors" to="/boutique">Boutique</RouterLink>
        <span class="mx-2">/</span>
        <span class="text-white">{{ product.name || product.title }}</span>
      </nav>

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

        <!-- Product Image -->
        <div class="lg:col-span-5">
          <div class="aspect-square rounded-3xl overflow-hidden flex items-center justify-center relative" style="background: linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(26, 26, 29, 0.8) 100%); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="absolute inset-0 opacity-50" style="background: radial-gradient(circle at 50% 50%, rgba(124, 58, 237, 0.15) 0%, transparent 70%);"></div>
            <img v-if="product.image" :alt="product.name" class="w-3/4 relative z-10" :src="product.image"/>
            <span v-else class="material-icons-outlined text-9xl text-white/20 relative z-10">subscriptions</span>
          </div>
        </div>

        <!-- Product Info -->
        <div class="lg:col-span-4 space-y-8">
          <div>
            <div class="flex items-center gap-3 mb-4">
              <div class="px-3 py-1 rounded-full text-xs font-semibold" style="background: rgba(55, 182, 233, 0.15); color: #37b6e9;">
                Abonnement Premium
              </div>
              <div class="flex items-center gap-1" style="color: #10B981;">
                <span class="material-icons text-sm">verified</span>
                <span class="text-sm font-medium">Officiel & Garanti</span>
              </div>
            </div>
            <h1 class="text-4xl lg:text-5xl font-black mb-4 text-white" style="font-family: 'Outfit', sans-serif;">
              {{ product.name || product.title }}
            </h1>
            <p class="text-white/60 text-lg leading-relaxed">
              {{ product.description }}
            </p>
          </div>

          <!-- Features -->
          <div v-if="product.features" class="space-y-3">
            <div v-for="feature in product.features" :key="feature" class="flex items-center gap-3 text-white/70">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center" style="background: rgba(55, 182, 233, 0.15);">
                <span class="material-icons text-sm" style="color: #37b6e9;">check</span>
              </div>
              <span>{{ feature }}</span>
            </div>
          </div>
        </div>

        <!-- Purchase Card -->
        <div class="lg:col-span-3">
          <div class="rounded-3xl p-8 sticky top-28" style="background: rgba(26, 26, 29, 0.8); backdrop-filter: blur(20px); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="mb-8">
              <div class="text-white/50 text-sm mb-2">Prix total</div>
              <div class="text-4xl font-black text-white" style="font-family: 'Outfit', sans-serif;">
                {{ totalPrice }} CFA
              </div>
            </div>

            <div class="space-y-5">
              <!-- Duration Selector -->
              <div>
                <label class="block text-xs font-semibold text-white/50 mb-3 uppercase tracking-wider">Durée</label>
                <div class="grid grid-cols-2 gap-2">
                  <button
                    v-for="duration in durations"
                    :key="duration"
                    @click="selectedDuration = duration"
                    class="py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300"
                    :class="selectedDuration === duration ? 'text-white' : 'text-white/60 hover:text-white hover:bg-white/5'"
                    :style="selectedDuration === duration ? 'background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%); box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);' : 'background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);'"
                  >
                    {{ duration }}
                  </button>
                </div>
              </div>

              <!-- Quantity Selector -->
              <div>
                <label class="block text-xs font-semibold text-white/50 mb-3 uppercase tracking-wider">Quantité</label>
                <div class="flex items-center gap-3 p-1 rounded-xl" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);">
                  <button @click="quantity = Math.max(1, quantity - 1)" class="w-10 h-10 flex items-center justify-center rounded-lg transition-all hover:scale-105" style="background: rgba(255, 255, 255, 0.05);">
                    <span class="material-icons-outlined text-sm text-white">remove</span>
                  </button>
                  <span class="font-semibold text-white w-8 text-center">{{ quantity }}</span>
                  <button @click="quantity++" class="w-10 h-10 flex items-center justify-center rounded-lg transition-all hover:scale-105" style="background: rgba(255, 255, 255, 0.05);">
                    <span class="material-icons-outlined text-sm text-white">add</span>
                  </button>
                </div>
              </div>

              <!-- Action Buttons -->
              <button @click="addToCart" class="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-xl active:scale-95" style="background: linear-gradient(135deg, #37b6e9 0%, #2e9ac4 100%); box-shadow: 0 4px 20px rgba(55, 182, 233, 0.3);">
                <span class="material-icons">add_shopping_cart</span>
                <span>Ajouter au panier</span>
              </button>
              <button @click="buyNow" class="w-full py-4 rounded-2xl font-bold text-white transition-all duration-300 hover:scale-[1.02] active:scale-95" style="background: linear-gradient(135deg, #4b4ced 0%, #8b5cf6 50%, #4b4ced 100%); color: #FFFFFF;">
                Commander maintenant
              </button>
            </div>

            <!-- Trust Badge -->
            <div class="mt-8 pt-6 flex items-center justify-center gap-2 text-white/40 text-sm" style="border-top: 1px solid rgba(255, 255, 255, 0.06);">
              <span class="material-icons text-base" style="color: #10B981;">lock</span>
              <span>Paiement 100% sécurisé</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Related Products -->
      <section v-if="relatedProducts.length > 0" class="mt-24">
        <h2 class="text-2xl font-bold mb-10 text-white flex items-center gap-3" style="font-family: 'Outfit', sans-serif;">
          <span class="material-icons" style="color: #37b6e9;">auto_awesome</span>
          Produits similaires
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
          <RouterLink
            v-for="related in relatedProducts"
            :key="related.id"
            :to="`/produit/${related.id}`"
            class="group cursor-pointer"
          >
            <div class="aspect-square rounded-2xl overflow-hidden mb-4 transition-all duration-300 transform group-hover:-translate-y-2 group-hover:shadow-2xl flex items-center justify-center relative" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);">
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style="background: radial-gradient(circle at 50% 50%, rgba(55, 182, 233, 0.15) 0%, transparent 70%);"></div>
              <img v-if="related.image" :alt="related.name" class="w-full h-full object-contain p-6 relative z-10" :src="related.image"/>
              <span v-else class="material-icons-outlined text-4xl text-white/20 relative z-10">subscriptions</span>
            </div>
            <h3 class="font-semibold text-white group-hover:text-white/80 transition-colors text-sm" style="font-family: 'Outfit', sans-serif;">{{ related.name || related.title }}</h3>
            <p class="font-bold mt-1 text-sm" style="color: #37b6e9;">{{ related.priceDisplay || `${related.price} CFA` }}</p>
          </RouterLink>
        </div>
      </section>

    </div>
  </div>
</template>
